// Smart Header Color Detection - Letter-by-Letter Precision
// lh-update: Enhanced mobile support and performance optimizations
class SmartHeader {
  constructor() {
    this.header = null;
    this.currentMode = null;
    this.observers = [];
    this.sections = [];
    this.isProcessing = false;
    this.rafId = null;
    this.trackedElements = [];
    this.trackedLetters = [];
    this.elementStates = new Map();
    this.letterStates = new Map();
    this.letterPrecisionEnabled = true; // Enable letter-level precision
    this.isMobile = window.innerWidth <= 768;
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.header = this.findHeader();
    
      if (!this.header) {
      console.warn('[SmartHeader] Header element not found');
        return;
      }

    console.log(`[SmartHeader] Initialized with letter-by-letter precision (Mobile: ${this.isMobile})`);

      this.header.classList.add('smart-header');
    
    this.setupIntersectionObserver();
    this.findTrackableSections();
    this.findTrackableElements();
    
    // Wrap text into individual letters (disable on mobile for performance)
    if (this.letterPrecisionEnabled && !this.isMobile) {
      this.wrapLettersInElements();
    }
    
    this.scheduleUpdate();
    
    window.addEventListener('scroll', () => this.scheduleUpdate(), { passive: true });
    window.addEventListener('resize', () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 768;
      
      // If switching between mobile and desktop, reinitialize
      if (wasMobile !== this.isMobile) {
        this.reinitializeForMobile();
      }
      
      this.scheduleUpdate();
    }, { passive: true });
    
    // Less frequent updates on mobile for better performance
    const updateInterval = this.isMobile ? 3000 : 2000;
    setInterval(() => {
      this.findTrackableSections();
      this.findAndTrackIcons(); // Re-scan for dynamically added icons
    }, updateInterval);
  }

  reinitializeForMobile() {
    console.log(`[SmartHeader] Reinitializing for ${this.isMobile ? 'mobile' : 'desktop'}`);
    
    // Remove smart-header class from old header
    if (this.header) {
      this.header.classList.remove('smart-header');
    }
    
    // Clear existing tracked elements and letters
    this.trackedElements = [];
    this.trackedLetters = [];
    this.elementStates.clear();
    this.letterStates.clear();
    
    // Find the correct header for current screen size
    this.header = this.findHeader();
    
    if (this.header) {
      this.header.classList.add('smart-header');
      console.log(`[SmartHeader] Found header:`, this.header.id || this.header.className);
    } else {
      console.warn('[SmartHeader] No header found for current screen size');
    }
    
    // Re-find elements
    this.findTrackableElements();
    
    // Re-wrap letters only on desktop
    if (this.letterPrecisionEnabled && !this.isMobile) {
      this.wrapLettersInElements();
    }
    
    // Schedule update
    this.scheduleUpdate();
  }

  findHeader() {
    // Prioritize mobile header on mobile, desktop header on desktop
    const selectors = this.isMobile ? [
      '#header__wrapper',
      '.header__wrapper',
      '#header__wrapper--md',
      '.header__wrapper--md',
      '[data-position="sticky"]',
      '[data-position="fixed"]',
      'header[class*="sticky"]',
      'header[class*="fixed"]',
      '.header--sticky',
      '.header--fixed'
    ] : [
      '#header__wrapper--md',
      '.header__wrapper--md',
      '#header__wrapper',
      '.header__wrapper',
      '[data-position="sticky"]',
      '[data-position="fixed"]',
      'header[class*="sticky"]',
      'header[class*="fixed"]',
      '.header--sticky',
      '.header--fixed'
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        // Check if element is visible (not hidden by CSS)
        const style = window.getComputedStyle(element);
        if (style.display !== 'none' && style.visibility !== 'hidden' && parseFloat(style.opacity) > 0) {
          return element;
        }
      }
    }

    // Fallback: look for any visible header element
    const headers = document.querySelectorAll('header');
    for (const header of headers) {
      const style = window.getComputedStyle(header);
      if ((style.position === 'sticky' || style.position === 'fixed') && 
          style.display !== 'none' && style.visibility !== 'hidden' && parseFloat(style.opacity) > 0) {
        return header;
      }
    }

    // Last resort: return any visible header
    for (const header of headers) {
      const style = window.getComputedStyle(header);
      if (style.display !== 'none' && style.visibility !== 'hidden' && parseFloat(style.opacity) > 0) {
        return header;
      }
    }

    return null;
  }

  findTrackableElements() {
    if (!this.header) return;

    const selectors = [
      '.header__navigation-link',
      '.header__navigation a',
      'nav a',
      'nav button',
      '.nav-link',
      '.menu-link',
      '.header__action',
      '.header__text',
      '[class*="header-action"]',
      '.menu-toggle',
      '.cart-count',
      '.cart-toggle',
      '.search-toggle',
      '.hamburger',
      '.mobile-menu-toggle',
      '.mobile-nav a',
      '.mobile-nav button'
    ];

    const newElements = [];
    const seenElements = new Set(this.trackedElements);

    selectors.forEach(selector => {
      try {
        this.header.querySelectorAll(selector).forEach(el => {
          if (!seenElements.has(el) && this.isValidTrackableElement(el)) {
            newElements.push(el);
            el.classList.add('smart-element');
            el.setAttribute('data-smart-tracking', 'true');
          }
        });
      } catch (e) {
        // Ignore
      }
    });

    if (newElements.length > 0) {
      this.trackedElements.push(...newElements);
      console.log(`[SmartHeader] Tracking ${this.trackedElements.length} text elements`);
    }

    // Track icons, SVGs and logos separately (no letter wrapping)
    this.findAndTrackIcons();
  }

  findAndTrackIcons() {
    if (!this.header) return;
    
    const iconSelectors = [
      '.logo',
      '.logo img',
      '[class*="logo"] img',
      'img[alt*="logo" i]',
      '.icon',
      '[class*="icon"]',
      'svg',
      '.header__action svg',
      '.cart-toggle svg',
      '.search-toggle svg',
      'button svg',
      'a svg',
      '.mobile-menu-toggle svg',
      '.hamburger svg',
      '.mobile-nav svg'
    ];

    let iconCount = 0;
    iconSelectors.forEach(selector => {
      try {
        this.header.querySelectorAll(selector).forEach(el => {
          // Don't re-track
          if (el.hasAttribute('data-smart-icon')) return;
          
          // Skip excluded elements
          if (this.isExcludedElement(el)) return;
          
          el.classList.add('smart-icon');
          el.setAttribute('data-smart-icon', 'true');
          iconCount++;
        });
      } catch (e) {
        // Ignore
      }
    });

    if (iconCount > 0) {
      console.log(`[SmartHeader] Tracking ${iconCount} icons/SVGs`);
    }
  }

  isValidTrackableElement(element) {
    if (element.hasAttribute('data-smart-tracking')) return false;
    
    // Skip images and SVGs for text wrapping
    if (element.tagName === 'IMG' || element.tagName === 'SVG') return false;
    
    // Skip excluded elements (exceptions)
    if (this.isExcludedElement(element)) return false;
    
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      return false;
    }
    
    // Must have text content
    const text = element.textContent.trim();
    if (!text) return false;
    
    return true;
  }

  isExcludedElement(element) {
    // List of elements that should not change color
    const excludedSelectors = [
      // Removed header-cart-count exceptions to allow smart header functionality
    ];
    
    // Check if element matches excluded selectors
    for (const selector of excludedSelectors) {
      try {
        if (element.matches(selector)) return true;
        // Also check if element is inside an excluded element
        if (element.closest(selector)) return true;
      } catch (e) {
        // Invalid selector, skip
      }
    }
    
    // Removed cart-count specific exclusions to allow smart header functionality
    
    return false;
  }

  wrapLettersInElements() {
    console.log('[SmartHeader] Wrapping letters for precision detection...');
    
    this.trackedElements.forEach(element => {
      // Skip if already wrapped
      if (element.hasAttribute('data-letters-wrapped')) return;
      
      // Get all text nodes
      this.wrapTextNodesInElement(element);
      
      element.setAttribute('data-letters-wrapped', 'true');
    });

    console.log(`[SmartHeader] Wrapped letters in ${this.trackedElements.length} elements`);
  }

  wrapTextNodesInElement(element) {
    // Don't wrap text in excluded elements
    if (this.isExcludedElement(element)) return;
    
    const textNodes = this.getTextNodes(element);
    
    textNodes.forEach(textNode => {
      const text = textNode.textContent;
      if (!text.trim()) return; // Skip empty text nodes
      
      // Check if parent is excluded
      if (textNode.parentNode && this.isExcludedElement(textNode.parentNode)) return;
      
      const fragment = document.createDocumentFragment();
      
      // Split into characters and wrap each
      Array.from(text).forEach((char) => {
        if (char === ' ') {
          // Preserve spaces
          const space = document.createTextNode(' ');
          fragment.appendChild(space);
        } else {
          const span = document.createElement('span');
          span.className = 'smart-letter';
          span.textContent = char;
          span.setAttribute('data-letter', char);
          span.style.display = 'inline-block';
          span.style.transition = 'color 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
          fragment.appendChild(span);
          
          // Track this letter
          this.trackedLetters.push(span);
        }
      });
      
      // Replace text node with wrapped letters
      if (textNode.parentNode) {
        textNode.parentNode.replaceChild(fragment, textNode);
      }
    });
  }

  getTextNodes(element) {
    const textNodes = [];
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          // Skip if parent is already a smart-letter or is SVG
          if (node.parentNode.classList?.contains('smart-letter')) {
            return NodeFilter.FILTER_REJECT;
          }
          if (node.parentNode.tagName === 'SVG' || node.parentNode.tagName === 'SCRIPT') {
            return NodeFilter.FILTER_REJECT;
          }
          // Only accept nodes with actual text
          if (node.textContent.trim().length > 0) {
            return NodeFilter.FILTER_ACCEPT;
          }
          return NodeFilter.FILTER_REJECT;
        }
      }
    );

    let node;
    while (node = walker.nextNode()) {
      textNodes.push(node);
    }
    
    return textNodes;
  }

  setupIntersectionObserver() {
    const observerOptions = {
      root: null,
      rootMargin: '-50px 0px -50px 0px',
      threshold: [0, 0.1, 0.5, 0.9, 1]
    };

    this.intersectionObserver = new IntersectionObserver(
      (entries) => this.handleIntersection(entries),
      observerOptions
    );
  }

  findTrackableSections() {
    const sectionSelectors = [
      'section',
      '[class*="hero"]',
      '[class*="banner"]',
      '[class*="section"]',
      'main > div',
      '[data-section-type]',
      '.shopify-section'
    ];

    const newSections = [];
    sectionSelectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => {
        if (!this.sections.includes(el) && el !== this.header && !this.header.contains(el)) {
          newSections.push(el);
          this.intersectionObserver.observe(el);
        }
      });
    });

    if (newSections.length > 0) {
      this.sections.push(...newSections);
    }
  }

  handleIntersection(entries) {
    this.scheduleUpdate();
  }

  scheduleUpdate() {
    if (this.rafId) return;
    
    this.rafId = requestAnimationFrame(() => {
      this.updateAllElements();
      this.rafId = null;
    });
  }

  updateAllElements() {
    if (!this.header || this.isProcessing) return;
    
    this.isProcessing = true;

    try {
      // Update global header background
      this.updateHeaderColor();
      
      // Update each letter individually (desktop) or elements (mobile)
      if (this.letterPrecisionEnabled && !this.isMobile) {
        this.updateAllLetters();
      } else {
        // Fallback to element-level (mobile or disabled precision)
        this.trackedElements.forEach(element => {
          if (document.body.contains(element) && this.isElementVisible(element)) {
            this.updateElementColor(element);
          }
        });
      }

      // Update non-text elements (logos, icons)
      this.updateNonTextElements();
      
    } catch (error) {
      console.error('[SmartHeader] Error updating elements:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  updateAllLetters() {
    // Batch process letters for performance
    const batchSize = 50;
    let processed = 0;
    
    const processLetterBatch = () => {
      const batch = this.trackedLetters.slice(processed, processed + batchSize);
      
      batch.forEach(letter => {
        if (!document.body.contains(letter)) return;
        if (!this.isElementVisible(letter)) return;
        
        this.updateLetterColor(letter);
      });
      
      processed += batchSize;
      
      // Continue processing if more letters remain
      if (processed < this.trackedLetters.length) {
        requestAnimationFrame(processLetterBatch);
      }
    };
    
    processLetterBatch();
  }

  updateLetterColor(letter) {
    const rect = letter.getBoundingClientRect();
    
    // Get center point of the letter
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    // Detect at multiple points for better accuracy
    const points = [
      { x: centerX, y: rect.bottom + 5 },
      { x: centerX, y: rect.bottom + 15 },
      { x: centerX, y: rect.bottom + 25 }
    ];
    
    const colorVotes = [];
    
    points.forEach(point => {
      const color = this.detectColorAtPoint(point.x, point.y);
      if (color) {
        colorVotes.push(color);
      }
    });
    
    const mode = this.determineModeFromVotes(colorVotes);
    const currentMode = this.letterStates.get(letter);
    
    if (mode !== currentMode) {
      this.setLetterMode(letter, mode);
      this.letterStates.set(letter, mode);
    }
  }

  setLetterMode(letter, mode) {
    // Remove existing classes
    letter.classList.remove('letter-on-light', 'letter-on-dark');
    
    // Add new mode class
    letter.classList.add(`letter-${mode}`);
    
    // Set color directly for immediate visual feedback
    if (mode === 'on-light') {
      letter.style.color = '#000000';
    } else {
      letter.style.color = '#ffffff';
    }
  }

  updateNonTextElements() {
    const icons = this.header.querySelectorAll('[data-smart-icon]');
    
    icons.forEach(element => {
      if (!document.body.contains(element) || !this.isElementVisible(element)) return;
      
      const rect = element.getBoundingClientRect();
      
      // Skip if not visible
      if (rect.width === 0 || rect.height === 0) return;
      
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const points = [
        { x: centerX, y: rect.bottom + 5 },
        { x: centerX, y: rect.bottom + 15 },
        { x: centerX, y: rect.bottom + 25 },
        { x: centerX, y: centerY } // Also check at center
      ];
      
      const colorVotes = [];
      points.forEach(point => {
        const color = this.detectColorAtPoint(point.x, point.y);
        if (color) colorVotes.push(color);
      });
      
      const mode = this.determineModeFromVotes(colorVotes);
      
      // Apply color based on element type
      if (element.tagName === 'IMG') {
        this.handleImageMode(element, mode);
      } else if (element.tagName === 'SVG') {
        this.handleSVGMode(element, mode);
      } else if (element.classList.contains('icon') || element.hasAttribute('class') && element.className.includes('icon')) {
        this.handleIconMode(element, mode);
      }
    });
  }

  handleImageMode(img, mode) {
    const src = img.src.toLowerCase();
    const alt = img.alt?.toLowerCase() || '';
    
    // Check if it's a logo
    const isLogo = img.classList.contains('logo') || 
                   img.closest('.logo') ||
                   alt.includes('logo') ||
                   src.includes('logo');
    
    // Reset filter
    img.style.filter = '';
    img.style.transition = 'filter 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
    
    if (!isLogo) return; // Only invert logos
    
    // Determine if logo should be inverted based on mode and filename
    const hasWhiteInName = src.includes('white') || src.includes('light') || alt.includes('white');
    const hasBlackInName = src.includes('black') || src.includes('dark') || alt.includes('black');
    
    if (mode === 'on-dark') {
      // Light background (on-dark means light text/dark background but wait, let me check)
      // Actually on-dark means the element is ON a dark background, so it needs light color
      // So light background = on-light, dark background = on-dark
      
      // on-dark = dark background, need light logo
      if (hasBlackInName) {
        // Logo is black, invert to white
        img.style.filter = 'invert(1) brightness(1.5)';
      } else if (!hasWhiteInName) {
        // Unknown logo type, assume it's dark and needs inversion
        img.style.filter = 'invert(1) brightness(1.2)';
      }
    } else {
      // on-light = light background, need dark logo
      if (hasWhiteInName) {
        // Logo is white, invert to black
        img.style.filter = 'invert(1) brightness(0.3)';
      } else if (hasBlackInName) {
        // Logo is already black, keep it
        img.style.filter = 'none';
      }
      // If neither, assume logo is dark and leave it as is
    }
  }

  handleSVGMode(svg, mode) {
    const color = mode === 'on-light' ? '#000000' : '#ffffff';
    
    // Check if it's a logo SVG
    const isLogo = svg.classList.contains('logo') || 
                   svg.closest('.logo') ||
                   svg.getAttribute('class')?.includes('logo') ||
                   svg.id?.toLowerCase().includes('logo');
    
    // Add transition
    svg.style.transition = 'fill 0.3s ease, stroke 0.3s ease, filter 0.3s ease';
    
    // For logos, we can use filter invert like images
    if (isLogo) {
      // Check current colors to determine if inversion is needed
      const firstPath = svg.querySelector('path, circle, rect');
      if (firstPath) {
        const currentFill = window.getComputedStyle(firstPath).fill;
        const rgb = this.parseRgbColor(currentFill);
        
        if (rgb) {
          const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
          const isDark = luminance < 0.5;
          
          // Invert if needed
          if (mode === 'on-dark' && isDark) {
            // Dark logo on dark background - invert to light
            svg.style.filter = 'invert(1) brightness(1.5)';
            return;
          } else if (mode === 'on-light' && !isDark) {
            // Light logo on light background - invert to dark
            svg.style.filter = 'invert(1) brightness(0.5)';
            return;
          } else {
            svg.style.filter = 'none';
          }
        }
      }
    }
    
    // For non-logo SVGs or if inversion didn't work, change fill/stroke directly
    svg.style.filter = 'none';
    svg.style.color = color;
    svg.style.fill = color;
    svg.style.stroke = color;
    
    // Update all child elements
    svg.querySelectorAll('path, circle, rect, line, polyline, polygon, ellipse').forEach(el => {
      el.style.transition = 'fill 0.3s ease, stroke 0.3s ease';
      
      const currentFill = el.getAttribute('fill');
      const currentStroke = el.getAttribute('stroke');
      
      // Don't override 'none' values
      if (currentFill && currentFill !== 'none') {
        el.style.fill = color;
      } else if (!currentFill || currentFill === 'currentColor') {
        el.style.fill = color;
      }
      
      if (currentStroke && currentStroke !== 'none') {
        el.style.stroke = color;
      } else if (!currentStroke || currentStroke === 'currentColor') {
        el.style.stroke = color;
      }
    });
  }

  handleIconMode(icon, mode) {
    const color = mode === 'on-light' ? '#000000' : '#ffffff';
    icon.style.color = color;
    
    // If icon contains SVG, update it too
    const svgs = icon.querySelectorAll('svg');
    svgs.forEach(svg => this.handleSVGMode(svg, mode));
  }

  isElementVisible(element) {
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && 
           style.visibility !== 'hidden' && 
           parseFloat(style.opacity) > 0;
  }

  updateElementColor(element) {
    const rect = element.getBoundingClientRect();
    const detectionPoints = this.getElementDetectionPoints(rect);
    const colorVotes = [];

    detectionPoints.forEach(point => {
      const color = this.detectColorAtPoint(point.x, point.y);
      if (color) {
        colorVotes.push(color);
      }
    });

    const mode = this.determineModeFromVotes(colorVotes);
    const currentMode = this.elementStates.get(element);
    
    if (mode !== currentMode) {
      this.setElementMode(element, mode);
      this.elementStates.set(element, mode);
    }
  }

  getElementDetectionPoints(rect) {
    const points = [];
    const centerX = rect.left + rect.width / 2;
    const bottom = rect.bottom;
    const offsets = [5, 15, 25];
    
    offsets.forEach(offset => {
      points.push({ x: centerX, y: bottom + offset });
      points.push({ x: rect.left + rect.width * 0.3, y: bottom + offset });
      points.push({ x: rect.left + rect.width * 0.7, y: bottom + offset });
    });

    return points;
  }

  updateHeaderColor() {
    if (!this.header) return;

    const headerRect = this.header.getBoundingClientRect();
    const detectionPoints = this.getDetectionPoints(headerRect);
    const colorVotes = [];

    detectionPoints.forEach(point => {
      const color = this.detectColorAtPoint(point.x, point.y);
      if (color) {
        colorVotes.push(color);
      }
    });

    const finalMode = this.determineModeFromVotes(colorVotes);
    
    if (finalMode !== this.currentMode) {
      this.setHeaderMode(finalMode);
    }
  }

  getDetectionPoints(headerRect) {
    const points = [];
    const bottom = headerRect.bottom;
    const verticalOffsets = [5, 15, 30];
    
    const horizontalPositions = [
      headerRect.left + headerRect.width * 0.1,
      headerRect.left + headerRect.width * 0.3,
      headerRect.left + headerRect.width * 0.5,
      headerRect.left + headerRect.width * 0.7,
      headerRect.left + headerRect.width * 0.9
    ];

    horizontalPositions.forEach(x => {
      verticalOffsets.forEach(offset => {
        points.push({ x, y: bottom + offset });
      });
    });

    return points;
  }

  detectColorAtPoint(x, y) {
    try {
      const elements = document.elementsFromPoint(x, y);
      
      const targetElement = elements.find(el => 
      el !== this.header && 
      !this.header.contains(el) &&
      el !== document.documentElement &&
        el !== document.body
      );
      
      if (!targetElement) return null;
      
        const bgColor = this.getElementBackgroundColor(targetElement);
      
      if (bgColor && !this.isTransparent(bgColor)) {
        return {
          color: bgColor,
          mode: this.getContrastingMode(bgColor),
          element: targetElement
        };
      }
    } catch (error) {
      // Silent fail
    }

    return null;
  }

  getElementBackgroundColor(element) {
    if (element.tagName === 'VIDEO' || element.tagName === 'IFRAME') {
      return 'rgb(0, 0, 0)';
    }

    const bgColor = this.getColorFromClasses(element);
    if (bgColor) return bgColor;

    const computedStyle = window.getComputedStyle(element);
    let backgroundColor = computedStyle.backgroundColor;
    
    if (this.isTransparent(backgroundColor)) {
      backgroundColor = this.getParentBackgroundColor(element);
    }

    if (this.isTransparent(backgroundColor) && computedStyle.backgroundImage !== 'none') {
      return 'rgb(0, 0, 0)';
    }

    return backgroundColor;
  }

  getColorFromClasses(element) {
    const classList = Array.from(element.classList);

    const bgClass = classList.find(cls => cls.startsWith('bg-'));
    if (bgClass) {
      const color = this.getColorFromTailwindClass(bgClass);
      if (color) return color;
    }

    const schemeClass = classList.find(cls => cls.startsWith('scheme-'));
    if (schemeClass) {
      const color = this.getColorFromSchemeClass(schemeClass);
      if (color) return color;
    }

    if (classList.some(cls => /dark|black|night/i.test(cls))) {
      return 'rgb(0, 0, 0)';
    }
    if (classList.some(cls => /light|white|bright/i.test(cls))) {
      return 'rgb(255, 255, 255)';
    }

    return null;
  }

  getParentBackgroundColor(element) {
    let parent = element.parentElement;
    let depth = 0;
    const maxDepth = 10;

    while (parent && parent !== document.body && depth < maxDepth) {
      const classColor = this.getColorFromClasses(parent);
      if (classColor) return classColor;

      const parentStyle = window.getComputedStyle(parent);
      const parentBg = parentStyle.backgroundColor;
      
      if (!this.isTransparent(parentBg)) {
        return parentBg;
      }

      if (parentStyle.backgroundImage !== 'none') {
        return 'rgb(0, 0, 0)';
      }

      parent = parent.parentElement;
      depth++;
    }

    return null;
  }

  isTransparent(color) {
    if (!color) return true;
    return color === 'rgba(0, 0, 0, 0)' || 
           color === 'transparent' || 
           color === 'initial' || 
           color === 'inherit' ||
           color === '' ||
           color === 'none';
  }

  getColorFromTailwindClass(className) {
    const colorMap = {
      'bg-black': 'rgb(0, 0, 0)',
      'bg-white': 'rgb(255, 255, 255)',
      'bg-gray-50': 'rgb(249, 250, 251)',
      'bg-gray-100': 'rgb(243, 244, 246)',
      'bg-gray-200': 'rgb(229, 231, 235)',
      'bg-gray-300': 'rgb(209, 213, 219)',
      'bg-gray-400': 'rgb(156, 163, 175)',
      'bg-gray-500': 'rgb(107, 114, 128)',
      'bg-gray-600': 'rgb(75, 85, 99)',
      'bg-gray-700': 'rgb(55, 65, 81)',
      'bg-gray-800': 'rgb(31, 41, 55)',
      'bg-gray-900': 'rgb(17, 24, 39)',
      'bg-slate-50': 'rgb(248, 250, 252)',
      'bg-slate-100': 'rgb(241, 245, 249)',
      'bg-slate-800': 'rgb(30, 41, 59)',
      'bg-slate-900': 'rgb(15, 23, 42)',
      'bg-zinc-800': 'rgb(39, 39, 42)',
      'bg-zinc-900': 'rgb(24, 24, 27)',
      'bg-neutral-800': 'rgb(38, 38, 38)',
      'bg-neutral-900': 'rgb(23, 23, 23)',
      'bg-stone-800': 'rgb(41, 37, 36)',
      'bg-stone-900': 'rgb(28, 25, 23)',
    };
    return colorMap[className] || null;
  }

  getColorFromSchemeClass(className) {
    const schemeMap = {
      'scheme-1': 'rgb(255, 255, 255)',
      'scheme-2': 'rgb(17, 24, 39)',
      'scheme-3': 'rgb(249, 250, 251)',
      'scheme-4': 'rgb(31, 41, 55)',
      'scheme-light': 'rgb(255, 255, 255)',
      'scheme-dark': 'rgb(0, 0, 0)',
    };
    return schemeMap[className] || null;
  }

  getContrastingMode(backgroundColor) {
    if (!backgroundColor) return 'on-dark';

    const rgb = this.parseRgbColor(backgroundColor);
    if (!rgb) return 'on-dark';

    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    
    return luminance > 0.5 ? 'on-light' : 'on-dark';
  }

  parseRgbColor(colorString) {
    const match = colorString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (match) {
      return {
        r: parseInt(match[1]),
        g: parseInt(match[2]),
        b: parseInt(match[3])
      };
    }

    const hexMatch = colorString.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    if (hexMatch) {
      return {
        r: parseInt(hexMatch[1], 16),
        g: parseInt(hexMatch[2], 16),
        b: parseInt(hexMatch[3], 16)
      };
    }

    return null;
  }

  determineModeFromVotes(votes) {
    if (votes.length === 0) return 'on-dark';

    const modeCount = {};
    votes.forEach(vote => {
      modeCount[vote.mode] = (modeCount[vote.mode] || 0) + 1;
    });

    let winningMode = 'on-dark';
    let maxVotes = 0;

    Object.entries(modeCount).forEach(([mode, count]) => {
      if (count > maxVotes) {
        maxVotes = count;
        winningMode = mode;
      }
    });

    const consensusThreshold = votes.length * 0.4;
    if (maxVotes >= consensusThreshold) {
      return winningMode;
    }

    return this.currentMode || winningMode;
  }

  setElementMode(element, mode) {
    element.classList.remove('element-on-light', 'element-on-dark');
    element.classList.add(`element-${mode}`);
    element.setAttribute('data-color-mode', mode);
  }

  setHeaderMode(mode) {
    if (!this.header) return;

    this.currentMode = mode;
    
    this.header.classList.remove('on-light', 'on-dark', 'light-mode', 'dark-mode');
    this.header.classList.add(mode);
    
    if (mode === 'on-light') {
      this.header.classList.add('dark-mode');
    } else {
      this.header.classList.add('light-mode');
    }
    
    this.header.dispatchEvent(new CustomEvent('smartheader:modechange', {
      detail: { mode }
    }));
  }

  // Public API
  getDebugInfo() {
    const icons = this.header?.querySelectorAll('[data-smart-icon]') || [];
    const logos = this.header?.querySelectorAll('.logo, [class*="logo"], img[alt*="logo" i]') || [];
    
    return {
      header: this.header,
      currentMode: this.currentMode,
      sectionsTracked: this.sections.length,
      elementsTracked: this.trackedElements.length,
      lettersTracked: this.trackedLetters.length,
      iconsTracked: icons.length,
      logosTracked: logos.length,
      letterPrecision: this.letterPrecisionEnabled,
      isProcessing: this.isProcessing
    };
  }

  // Debug logo colors
  debugLogos() {
    const logos = this.header?.querySelectorAll('.logo, [class*="logo"], img[alt*="logo" i], .logo svg') || [];
    console.log(`[SmartHeader] Found ${logos.length} logos:`);
    
    logos.forEach((logo, index) => {
      const rect = logo.getBoundingClientRect();
      const style = window.getComputedStyle(logo);
      console.log(`Logo ${index + 1}:`, {
        element: logo,
        tagName: logo.tagName,
        className: logo.className,
        filter: style.filter,
        src: logo.src || 'N/A',
        visible: rect.width > 0 && rect.height > 0
      });
    });
  }

  toggleLetterPrecision(enabled) {
    this.letterPrecisionEnabled = enabled;
    if (enabled && this.trackedLetters.length === 0) {
      this.wrapLettersInElements();
    }
    console.log(`Letter precision ${enabled ? 'enabled' : 'disabled'}`);
  }

  forceMode(mode) {
    if (['on-light', 'on-dark'].includes(mode)) {
      this.setHeaderMode(mode);
      this.trackedLetters.forEach(letter => {
        this.setLetterMode(letter, mode);
        this.letterStates.set(letter, mode);
      });
    }
  }
}

// Initialize
const smartHeader = new SmartHeader();

window.smartHeader = smartHeader;

window.testSmartHeader = () => {
  console.log('Smart Header Debug Info:', smartHeader.getDebugInfo());
};

window.forceHeaderMode = (mode) => {
  smartHeader.forceMode(mode);
  console.log(`Forced all elements to ${mode} mode`);
};

window.toggleLetterPrecision = (enabled) => {
  smartHeader.toggleLetterPrecision(enabled);
};

window.debugLogos = () => {
  smartHeader.debugLogos();
};

window.forceMobileReinit = () => {
  console.log('[SmartHeader] Forcing mobile reinitialization...');
  smartHeader.reinitializeForMobile();
};

window.testCartCount = () => {
  console.log('=== Cart Count Test ===');
  
  const cartCountSelectors = [
    '#header-cart-count_md',
    '.header-cart-count_md',
    '[id*="cart-count"]',
    '[id*="header-cart-count"]',
    '.cart-count'
  ];
  
  cartCountSelectors.forEach(selector => {
    const elements = document.querySelectorAll(selector);
    console.log(`${selector}: ${elements.length} elements found`);
    
    elements.forEach((el, index) => {
      const style = window.getComputedStyle(el);
      const isInSmartHeader = el.closest('.smart-header');
      const smartHeaderMode = isInSmartHeader?.classList.contains('on-light') ? 'on-light' : 
                             isInSmartHeader?.classList.contains('on-dark') ? 'on-dark' : 'none';
      
      console.log(`  ${index + 1}. ID: ${el.id || 'No ID'}`);
      console.log(`     Color: ${style.color}`);
      console.log(`     Background: ${style.backgroundColor}`);
      console.log(`     In smart header: ${!!isInSmartHeader}`);
      console.log(`     Smart header mode: ${smartHeaderMode}`);
      console.log(`     Classes: ${el.className}`);
    });
  });
  
  // Test specific cart count elements
  console.log('\n=== Specific Cart Count Elements ===');
  const headerCartCount = document.getElementById('header-cart-count_md');
  if (headerCartCount) {
    const style = window.getComputedStyle(headerCartCount);
    console.log('header-cart-count_md found:');
    console.log('  Color:', style.color);
    console.log('  Background:', style.backgroundColor);
    console.log('  Computed color:', style.color);
  } else {
    console.log('header-cart-count_md not found');
  }
};

window.testMobileSmartHeader = () => {
  console.log('=== Mobile Smart Header Test ===');
  console.log('Screen width:', window.innerWidth);
  console.log('Is mobile:', smartHeader.isMobile);
  console.log('Header found:', !!smartHeader.header);
  console.log('Header ID:', smartHeader.header?.id || 'No ID');
  console.log('Header classes:', smartHeader.header?.className || 'No classes');
  console.log('Elements tracked:', smartHeader.trackedElements.length);
  console.log('Letters tracked:', smartHeader.trackedLetters.length);
  console.log('Current mode:', smartHeader.currentMode);
  console.log('Letter precision enabled:', smartHeader.letterPrecisionEnabled);
  
  // Test header detection
  const headerSelectors = [
    '#header__wrapper',
    '.header__wrapper',
    '#header__wrapper--md',
    '.header__wrapper--md'
  ];
  
  console.log('\n=== Header Detection Test ===');
  headerSelectors.forEach(selector => {
    const element = document.querySelector(selector);
    const isVisible = element ? window.getComputedStyle(element).display !== 'none' : false;
    console.log(`${selector}:`, element ? `✓ Found (visible: ${isVisible})` : '✗ Not found');
    if (element) {
      console.log(`  - ID: ${element.id || 'No ID'}`);
      console.log(`  - Classes: ${element.className || 'No classes'}`);
      console.log(`  - Display: ${window.getComputedStyle(element).display}`);
    }
  });
  
  console.log('\n=== Mobile Elements Test ===');
  const mobileSelectors = [
    '.hamburger',
    '.mobile-menu-toggle', 
    '.mobile-nav a',
    '.mobile-nav button',
    'nav a',
    'nav button'
  ];
  
  mobileSelectors.forEach(selector => {
    const elements = document.querySelectorAll(selector);
    console.log(`${selector}: ${elements.length} elements found`);
  });
  
  console.log('\n=== Smart Header Classes Test ===');
  const smartHeaders = document.querySelectorAll('.smart-header');
  console.log(`Smart header elements: ${smartHeaders.length}`);
  smartHeaders.forEach((el, index) => {
    console.log(`  ${index + 1}. ID: ${el.id || 'No ID'}, Classes: ${el.className}`);
  });
};

// lh-update: Cart bubble visibility management
class CartBubbleManager {
  constructor() {
    this.cartCountElements = [];
    this.init();
  }

  init() {
    // Find all cart count elements
    this.findCartCountElements();
    
    // Initial check
    this.updateCartBubbleVisibility();
    
    // Listen for cart updates
    this.setupCartListeners();
    
    // Periodic check for dynamic updates
    setInterval(() => {
      this.updateCartBubbleVisibility();
    }, 1000);
  }

  findCartCountElements() {
    const selectors = [
      '#header-cart-count_md',
      '.header-cart-count_md',
      '[id*="cart-count"]',
      '[id*="header-cart-count"]',
      '.cart-count',
      '.cart-bubble',
      '[class*="cart-count"]'
    ];

    this.cartCountElements = [];
    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        if (!this.cartCountElements.includes(el)) {
          this.cartCountElements.push(el);
        }
      });
    });

    console.log(`[CartBubbleManager] Found ${this.cartCountElements.length} cart count elements`);
  }

  setupCartListeners() {
    // Listen for Shopify cart events
    document.addEventListener('cart:updated', () => {
      setTimeout(() => this.updateCartBubbleVisibility(), 100);
    });

    document.addEventListener('cart:refresh', () => {
      setTimeout(() => this.updateCartBubbleVisibility(), 100);
    });

    // Listen for AJAX cart updates
    document.addEventListener('ajaxCart:updated', () => {
      setTimeout(() => this.updateCartBubbleVisibility(), 100);
    });

    // Listen for form submissions that might update cart
    document.addEventListener('submit', (e) => {
      if (e.target.matches('form[action*="cart"], form[action*="add"]')) {
        setTimeout(() => this.updateCartBubbleVisibility(), 500);
      }
    });
  }

  getCartCount() {
    // Try to get cart count from Shopify global object
    if (window.Shopify && window.Shopify.theme && window.Shopify.theme.cart) {
      return window.Shopify.theme.cart.item_count || 0;
    }

    // Try to get from cart object
    if (window.cart && typeof window.cart.item_count !== 'undefined') {
      return window.cart.item_count;
    }

    // Try to parse from cart count elements
    for (const element of this.cartCountElements) {
      const text = element.textContent.trim();
      const number = parseInt(text, 10);
      if (!isNaN(number)) {
        return number;
      }
    }

    // Try to get from meta tag
    const cartMeta = document.querySelector('meta[name="cart-count"]');
    if (cartMeta) {
      return parseInt(cartMeta.content, 10) || 0;
    }

    return 0;
  }

  updateCartBubbleVisibility() {
    const cartCount = this.getCartCount();
    
    this.cartCountElements.forEach(element => {
      if (cartCount === 0) {
        // Hide the bubble when count is 0
        element.style.display = 'none';
        element.style.visibility = 'hidden';
        element.setAttribute('aria-hidden', 'true');
        element.classList.add('cart-bubble-hidden');
      } else {
        // Show the bubble when count is > 0
        element.style.display = '';
        element.style.visibility = '';
        element.removeAttribute('aria-hidden');
        element.classList.remove('cart-bubble-hidden');
      }
    });

    // Also handle parent containers that might contain the cart bubble
    const cartContainers = document.querySelectorAll('.cart-toggle, .header__action--cart, [class*="cart"]');
    cartContainers.forEach(container => {
      const hasVisibleCount = container.querySelector('.cart-count:not(.cart-bubble-hidden)');
      if (hasVisibleCount) {
        container.classList.remove('cart-empty');
      } else {
        container.classList.add('cart-empty');
      }
    });

    console.log(`[CartBubbleManager] Cart count: ${cartCount}, ${this.cartCountElements.length} elements updated`);
  }

  // Public method to manually refresh
  refresh() {
    this.findCartCountElements();
    this.updateCartBubbleVisibility();
  }
}

// Initialize cart bubble manager
const cartBubbleManager = new CartBubbleManager();

// Make it globally available
window.cartBubbleManager = cartBubbleManager;

console.log('[SmartHeader] Letter-by-letter precision enabled! 🎨');
console.log('Debug commands:');
console.log('  - testSmartHeader() : voir les stats');
console.log('  - testMobileSmartHeader() : tester le support mobile');
console.log('  - testCartCount() : tester le cart-count');
console.log('  - forceMobileReinit() : forcer la réinitialisation mobile');
console.log('  - debugLogos() : voir les logos détectés');
console.log('  - forceHeaderMode("on-light"/"on-dark") : forcer un mode');
console.log('  - toggleLetterPrecision(false) : désactiver la précision lettre');
console.log('  - cartBubbleManager.refresh() : rafraîchir la bulle du panier');
